<script
  setup
  lang="ts"
  generic="TABLE extends PejerTableNames, LIST extends PejerTablesList<TABLE>, LValue"
>
import type { AccessorKeyColumnDef, ColumnDef } from '@tanstack/vue-table'
import useSearch, { type SearchableColumn } from 'pejer/src/useSearch'
import ColumnSearch from '../ColumnSearch.vue'

import { SearchIcon } from 'lucide-vue-next'
import { toast } from 'pejer/src/components/ui/toast'
import { LooseColumnDef, PejerTableNames, PejerTablesList } from 'pejer/src/types/base'
import { BaseQueryArgs, Filters, Order, SearchQuery } from 'pejer/src/types/types'
import { computed, onMounted, Ref, toRef, watch } from 'vue'
import { usePejerBase } from '../../stores/pejerData'
import usePejerTableList, {
  PejerTableListOptions,
  TableFeatureEnabled,
} from '../../usePejerTableList'
import ColoredHeadline from '../ColoredHeadline.vue'
import PaginationNavigation from '../PaginationNavigation.vue'
import SectionCard from '../ui/section-card/SectionCard.vue'
import SectionCardHeader from '../ui/section-card/SectionCardHeader.vue'
import DataGroupTable from './core/DataGroupTable.vue'
import DataTable from './core/DataTable.vue'

export type PejerTableListProps<TABLE extends PejerTableNames, LValue> = {
  table: TABLE
  columns?: ColumnDef<PejerTablesList<TABLE>, LValue>[]
  visibleColumns?: LooseColumnDef<PejerTablesList<TABLE>>[]
  searchableColumnList?: SearchableColumn<PejerTablesList<TABLE>>[]
  initialSearch?: SearchQuery<PejerTablesList<TABLE>>
  staticValues?: SearchQuery<PejerTablesList<TABLE>>
  options?: PejerTableListOptions<TABLE>
  urlKey?: string
  dropQueryParams?: boolean

  simpleTable?: boolean

  /**
   * These are the general options for the tables features
   */
  searchDisabled?: TableFeatureEnabled
  hidePagination?: TableFeatureEnabled
  cannotExpandRows?: boolean
  dontFillMissingEntries?: boolean

  simpleGroupBy?: (keyof SearchQuery<PejerTablesList<TABLE>>)[]

  /**
   * Styling options
   */
  minimalDesign?: boolean
  showAll?: boolean
}

const props = defineProps<PejerTableListProps<TABLE, LValue>>()
const emits = defineEmits<{
  (
    e: 'update:query',
    query: {
      formatted: Filters<PejerTablesList<TABLE>>
      raw: Filters<PejerTablesList<TABLE>>
    }
  ): void
}>()

function toggleSort(column: keyof PejerTablesList<TABLE>, direction: Order = 'Descending') {
  triggerSort(column, direction)
}

const store = usePejerBase()

function middlewareSearch(query: SearchQuery<PejerTablesList<TABLE>>) {
  search(query)
  emitQuery(query)
}

function middlewareResetData(query: SearchQuery<PejerTablesList<TABLE>>) {
  resetData(query)
  emitQuery(query)
}

function emitQuery(query: SearchQuery<PejerTablesList<TABLE>>) {
  const formatted = store.mapQueryToFormattedValues(props.table, query as any)
  emits('update:query', {
    raw: formatted.filters as Filters<PejerTablesList<TABLE>>,
    formatted: query as Filters<PejerTablesList<TABLE>>,
  })
}

const searchOptions = computed(() => ({
  resetPagination: middlewareResetData,
  autoCompleteManager: store.state.autoComplete,
  staticValues: props.staticValues,
  key: props.urlKey,
  initialSearch: props.initialSearch,
}))

const {
  searchableColumns,
  autoComplete,
  filterQuery,
  activeColumn,
  staticValues,
  updateQuery,
  updateAutoCompleteQuery,
  updateActiveColumn,
  removeColumn,
  resetQuery,
} = useSearch<any, any>(
  props.table,
  props.searchableColumnList ?? (store.getDefaultColumns(props.table).searchableColumns as any),
  middlewareSearch,
  toRef(props, 'staticValues'),
  searchOptions.value
)

const {
  data,
  isLoading,
  error,
  isFetching,
  isError,
  defaultColumnVisibilityState,
  query,
  currentPage,
  totalSize,
  currentlyKnownMinSize,
  initialColumnVisibilityState,
  decrementPage,
  incrementPage,
  updatePagination,
  search,
  resetData,
  refresh,
  triggerSort,
  updateColumnVisibilityState,
} = usePejerTableList(props.table, {
  default_query: {
    filters: {
      staticFilters: staticValues,
      userFilters: filterQuery,
    },
  },
  colVisibilityOptions: {
    defaultColumns: props.visibleColumns as string[],
  },
  ...(props.options ?? ({} as PejerTableListOptions<TABLE>)),
  urlKey: props.urlKey ?? props.options?.urlKey,
  pageSize: props.showAll === true ? 10000 : props.options?.pageSize,
})

function isValidQuery(query: SearchQuery<any>) {
  for (const key in filterQuery.value) {
    if (key === 'OR' || key === 'AND') {
      const subQueries = (filterQuery.value as any)[key] as SearchQuery<any>[]
      for (const subQuery of subQueries) {
        if (!isValidQuery(subQuery)) return false
      }
    } else if (
      !store.getSchemaObjectFromFlattenedKeys(store.config.schema.tables[props.table].list, key)
    )
      return false
  }

  return true
}

onMounted(() => {
  for (const key in filterQuery.value) {
    if (!isValidQuery) {
      toast({
        title: 'Error',
        description: 'Invalid filters in url, resetting filters...',
        variant: 'destructive',
      })
      resetQuery()
      return
    }
  }
  search(
    {
      filters: {
        userFilters: filterQuery.value,
        staticFilters: staticValues.value,
      },
    },
    false
  )
})

const reloadPages = () =>
  resetData({
    filters: {
      userFilters: filterQuery.value,
      staticFilters: staticValues.value,
    },
  })

watch(
  () => props.staticValues,
  () => {
    reloadPages()
  },
  { deep: true }
)

defineExpose({
  reloadPages,
  resetQuery,
})

const columns = (props.columns ?? store.columns[props.table].list) as AccessorKeyColumnDef<
  PejerTablesList<TABLE>,
  LValue
>[]

const listSchema = computed(() => store.getListSchema(props.table))
const mainGroup = computed(() => {
  return Object.values(listSchema.value.groups).find((group) => group.isMainGroup)
})

const rawQuery = computed(() => {
  return store.mapQueryToFormattedValues(props.table, query.value as any).filters
})
const querySlotProps = computed(() => ({
  raw: rawQuery,
  formatted: filterQuery as Ref<Filters<PejerTablesList<TABLE>>>,
}))
</script>
<template>
  <SectionCard
    class="relative flex flex-col gap-2 pt-0 px-0"
    :class="{
      'border-0 shadow-none bg-transparent border-none': props.minimalDesign,
    }"
  >
    <div
      class="sticky top-0 z-50 px-6 bg-white/90 backdrop-blur"
      :class="{
        'border shadow-sm border-slate-200 rounded-md py-4 top-0 ': props.minimalDesign,
      }"
      v-if="props.searchDisabled !== true"
    >
      <!-- v-if="props.searchDisabled !== true" -->
      <ColoredHeadline
        v-if="!minimalDesign"
        :color="mainGroup.color.accent"
        :text="listSchema.title"
        class="text-md font-semibold text-slate-500 pt-6 pb-2"
      >
        <template v-slot:icon>
          <component :is="mainGroup.icon" class="w-5 h-5 mr-2 text-slate-500" />
        </template>
      </ColoredHeadline>
      <slot name="table-description" />

      <div v-if="props.searchDisabled !== true && searchableColumns.length > 0">
        <SectionCardHeader v-if="minimalDesign" class="flex gap-1 items-center mb-1 text-primary">
          <SearchIcon class="h-4 w-4" /> Search
        </SectionCardHeader>
        <!-- prettier-ignore -->
        <ColumnSearch
        placeholder="Search..."
        :searchable-columns="searchableColumns"
        :autocomplete="autoComplete"
        :table="props.table"
        :active-column="(activeColumn as keyof LIST)"
        :js-schema="listSchema"
        :filter-query="filterQuery"
        :groups="store.config.schema.tables[props.table].groups"
        :multiple-lines="minimalDesign"
        
        @on-search="updateQuery"
        @on-input-change="updateAutoCompleteQuery"
        @on-active-column-change="updateActiveColumn"
        @on-remove-column="removeColumn"
       >
        <slot/> 
      </ColumnSearch>
      </div>
    </div>
    <slot name="graph" :query="querySlotProps" />

    <component
      :is="!minimalDesign ? 'div' : SectionCard"
      class="relative w-full h-full shrink-0 flex-1 min-h-full"
      :class="{
        'px-6 py-2': !minimalDesign,
      }"
    >
      <slot name="table-title" />
      <!-- prettier-ignore -->
      <slot name="table" :data="(data as unknown as LIST[])" :columns="columns" :query="querySlotProps">
        <!-- prettier-ignore -->
        <DataTable
          v-if="simpleTable === true"
          :columns="columns"
          :data="data"
          :table-name="props.table"
          :columns-visible-by-default="defaultColumnVisibilityState"
          :initial-column-visibility="initialColumnVisibilityState"
          :pagination="{ page: currentPage.page, size: currentPage.size }"
          :query="(query as BaseQueryArgs<PejerTablesList<TABLE>>)"
          :groups="store.config.schema.tables[props.table]?.groups"
          :can-expand-rows="!cannotExpandRows"
          
          :isLoading="isLoading"
          :error="error"
          :isError="isError"
          :isFetching="isFetching"

          :dont-fill-missing-entries="dontFillMissingEntries"
          :simple-group-by="props.simpleGroupBy"

          @update-column-visibility="updateColumnVisibilityState"
          @refresh="refresh"
          @triggerSort="toggleSort"
          @update-column-search="updateActiveColumn"
        >
          <template #group-header="{ groupValue }">
            <slot name="group-header" :group-value="groupValue" />
          </template>
        </DataTable>
        <DataGroupTable
          v-else
          :columns="columns"
          :data="data"
          :table-name="props.table"
          :columns-visible-by-default="defaultColumnVisibilityState"
          :initial-column-visibility="initialColumnVisibilityState"
          :pagination="{ page: currentPage.page, size: currentPage.size }"
          :query="(query as BaseQueryArgs<PejerTablesList<TABLE>>)"
          :groups="store.config.schema.tables[props.table]?.groups"
          :can-expand-rows="!cannotExpandRows"
          
          :isLoading="isLoading"
          :error="error"
          :isError="isError"
          :isFetching="isFetching"

          :dont-fill-missing-entries="dontFillMissingEntries"
          
          @update-column-visibility="updateColumnVisibilityState"
          @refresh="refresh"
          @triggerSort="toggleSort"
          @update-column-search="updateActiveColumn"
          >
          <!-- :simple-group-by="props.simpleGroupBy" -->
          <template #group-header="{ groupValue }">
            <slot name="group-header" :group-value="groupValue" />
          </template>
        </DataGroupTable>
      </slot>
      <slot name="after-table" />

      <div class="w-full bottom-0 sticky p-1 z-10 bg-white" v-if="!showAll">
        <PaginationNavigation
          class="bg-white p-2"
          :total-size="totalSize"
          :current-size="currentlyKnownMinSize"
          :pagination="currentPage"
          :isFetching="isFetching"
          @update-pagination="updatePagination"
          @increment-page="incrementPage"
          @decrement-page="decrementPage"
        />
      </div>
    </component>
  </SectionCard>
</template>
